import { Link } from 'react-router-dom';
import { ShoppingBag, Clock, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { toast } from '@/components/ui/sonner';
import { formatWindowDate } from '@/lib/windowDate';
import { money } from '@/lib/format';

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const upcoming = product.window_state === 'upcoming';
  const orderable = product.orderable !== false && product.window_open !== false && !upcoming;
  const opensOn = formatWindowDate(product.window_open_date);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (upcoming) {
      toast.info(opensOn ? `Opens on ${opensOn} — you can order then` : 'This vendor isn’t taking orders yet');
      return;
    }
    if (!orderable) {
      toast.error(`Order window for ${product.supplier_name || 'this supplier'} is closed`);
      return;
    }
    const result = addItem(product);
    if (result?.conflict) return;
    toast.success(`${product.name} added to cart`);
  };

  const bgColors = ['bg-secondary/30', 'bg-muted', 'bg-accent/15', 'bg-primary/10'];
  const bgColor = bgColors[index % bgColors.length];

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group animate-fade-up"
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative rounded-2xl overflow-hidden bg-card shadow-md hover:shadow-xl transition-all duration-300">
          <div className={`relative aspect-[4/5] ${bgColor}`}>
            {product.image_url && (
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            
            {product.featured && (
              <span className="absolute top-4 right-4 bg-gold text-foreground text-xs font-body font-bold px-3 py-1.5 rounded-md shadow-sm">
                Featured
              </span>
            )}

            {upcoming ? (
              <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-gold text-charcoal text-xs font-body font-bold px-3 py-1.5 rounded-md shadow-sm" data-testid={`upcoming-badge-${product.id}`}>
                <CalendarClock className="h-3 w-3" />
                {opensOn ? `Opens ${opensOn}` : 'Opening soon'}
              </div>
            ) : !orderable && (
              <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-charcoal/80 text-white text-xs font-body font-semibold px-3 py-1.5 rounded-md shadow-sm" data-testid={`window-closed-badge-${product.id}`}>
                <Clock className="h-3 w-3" />
                Window Closed
              </div>
            )}

            <div className="absolute bottom-4 right-4 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 sm:translate-y-2 sm:group-hover:translate-y-0">
              <Button
                onClick={handleAdd}
                data-testid={`add-to-cart-${product.id}`}
                disabled={!orderable}
                title={upcoming && opensOn ? `Opens on ${opensOn}` : undefined}
                className={`rounded-full shadow-lg h-11 w-11 p-0 transition-all duration-200 hover:scale-110 ${
                  orderable
                    ? 'bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light'
                    : 'bg-charcoal/40 text-white/70 cursor-not-allowed'
                }`}
                size="icon"
              >
                {upcoming ? <CalendarClock className="h-4 w-4" /> : <ShoppingBag className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="p-5 bg-card">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-xs font-body text-teal uppercase tracking-[0.15em] font-semibold">
                {product.supplier_name || product.category_name}
              </p>
            </div>
            <h3 className="font-heading text-lg font-medium text-foreground tracking-tight leading-tight mb-2">
              {product.name}
            </h3>
            <div className="flex items-center gap-3 mb-2">
              {product.sku && (
                <p className="text-xs font-body text-foreground/40">SKU: {product.sku}</p>
              )}
              {product.unit_size_value && product.unit_size_unit && (
                <span className="text-xs font-body text-teal-dark/70 bg-teal/8 px-2 py-0.5 rounded-full" data-testid={`unit-size-${product.id}`}>
                  {product.unit_size_value}{product.unit_size_unit}
                </span>
              )}
            </div>
            <p className="font-body text-xl font-bold text-foreground">
              R{money(product.price)}
            </p>
            {Number(product.moq || 0) > 1 && (
              <p className="text-xs font-body text-gold mt-1" data-testid={`moq-badge-${product.id}`}>
                Min order: {product.moq} units
              </p>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};
